/**
 * Full desk configuration system — ARCHITECTURE.md section 4.1.
 *
 * A desk config captures the institutional knowledge of an analyst team:
 * which sources to collect from, what patterns matter, what to ignore,
 * and how entities relate in a specific domain.
 *
 * Desk presets (desk-presets.ts) control the dashboard view (layers, panels).
 * Desk configs drive investigations (sources, indicators, geographic bounds).
 */

// ---------------------------------------------------------------------------
// Source reliability — NATO STANAG 2022 (ARCHITECTURE.md section 7.1)
// ---------------------------------------------------------------------------

export type SourceReliability = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';

// ---------------------------------------------------------------------------
// Desk config schema — ARCHITECTURE.md section 4.1
// ---------------------------------------------------------------------------

export interface DeskSource {
  id: string;
  name: string;
  type: 'rss' | 'api' | 'telegram_channel' | 'csv' | 'custom';
  url?: string;
  endpoint?: string;
  channelId?: string;
  reliability: SourceReliability;
  checkIntervalMinutes: number;
  enabled: boolean;
  notes?: string;
}

export interface PriorityIndicatorCondition {
  [key: string]: unknown;
}

export interface PriorityIndicator {
  id: string;
  description: string;
  entityType: string;
  conditions: PriorityIndicatorCondition;
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  alert: boolean;
}

export interface WatchlistEntry {
  type: string;
  name: string;
  identifiers?: Array<{ type: string; value: string }>;
  aliases?: string[];
}

export interface Exclusion {
  sourceDomain?: string;
  entityName?: string;
  reason: string;
}

export interface GeographicFocus {
  primary: {
    type: 'bbox';
    coords: [number, number, number, number]; // [south, west, north, east]
  };
  secondary?: Array<{
    name: string;
    type: 'radius';
    center: [number, number]; // [lat, lng]
    km: number;
  }>;
}

export interface OntologyExtensions {
  assetSubtypes?: string[];
  customRelationships?: Array<{
    type: string;
    source: string;
    target: string;
    description: string;
  }>;
}

export interface WebSource {
  url: string;
  name: string;
  reliability: 'A' | 'B';
  scrapePattern?: string;
}

export interface SearchConfig {
  searxngUrl?: string;  // default: http://localhost:8080
  maxResults?: { quick: number; standard: number; deep: number };
}

export interface DeskConfig {
  deskId: string;
  version: string;
  name: string;
  description: string;
  createdBy?: string;
  lastModified?: string;

  geographicFocus: GeographicFocus;
  sources: DeskSource[];
  priorityIndicators: PriorityIndicator[];
  entityWatchlists: WatchlistEntry[];
  exclusions: Exclusion[];
  ontologyExtensions: OntologyExtensions;

  /** Curated web sources for Layer 1 collection (desk-analyst-curated, reliability A-B). */
  webSources?: WebSource[];
  /** SearXNG search configuration for Layer 2 collection. */
  searchConfig?: SearchConfig;
}

// ---------------------------------------------------------------------------
// Desk config JSON shape (camelCase ↔ snake_case conversion)
// ---------------------------------------------------------------------------

/** Raw JSON shape matching the desk config files on disk. */
export interface DeskConfigJSON {
  desk_id: string;
  version: string;
  name: string;
  description: string;
  created_by?: string;
  last_modified?: string;

  geographic_focus: {
    primary: { type: 'bbox'; coords: [number, number, number, number] };
    secondary?: Array<{ name: string; type: 'radius'; center: [number, number]; km: number }>;
  };

  sources: Array<{
    id: string;
    name: string;
    type: 'rss' | 'api' | 'telegram_channel' | 'csv' | 'custom';
    url?: string;
    endpoint?: string;
    channel_id?: string;
    reliability: SourceReliability;
    check_interval_minutes: number;
    enabled: boolean;
    notes?: string;
  }>;

  priority_indicators: Array<{
    id: string;
    description: string;
    entity_type: string;
    conditions: Record<string, unknown>;
    priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
    alert: boolean;
  }>;

  entity_watchlists: Array<{
    type: string;
    name: string;
    identifiers?: Array<{ type: string; value: string }>;
    aliases?: string[];
  }>;

  exclusions: Array<{
    source_domain?: string;
    entity_name?: string;
    reason: string;
  }>;

  ontology_extensions: {
    asset_subtypes?: string[];
    custom_relationships?: Array<{
      type: string;
      source: string;
      target: string;
      description: string;
    }>;
  };

  web_sources?: Array<{
    url: string;
    name: string;
    reliability: 'A' | 'B';
    scrape_pattern?: string;
  }>;

  search_config?: {
    searxng_url?: string;
    max_results?: { quick: number; standard: number; deep: number };
  };
}

// ---------------------------------------------------------------------------
// JSON ↔ DeskConfig conversion
// ---------------------------------------------------------------------------

export function parseDeskConfigJSON(json: DeskConfigJSON): DeskConfig {
  return {
    deskId: json.desk_id,
    version: json.version,
    name: json.name,
    description: json.description,
    createdBy: json.created_by,
    lastModified: json.last_modified,
    geographicFocus: {
      primary: json.geographic_focus.primary,
      secondary: json.geographic_focus.secondary,
    },
    sources: json.sources.map(s => ({
      id: s.id,
      name: s.name,
      type: s.type,
      url: s.url,
      endpoint: s.endpoint,
      channelId: s.channel_id,
      reliability: s.reliability,
      checkIntervalMinutes: s.check_interval_minutes,
      enabled: s.enabled,
      notes: s.notes,
    })),
    priorityIndicators: json.priority_indicators.map(pi => ({
      id: pi.id,
      description: pi.description,
      entityType: pi.entity_type,
      conditions: pi.conditions,
      priority: pi.priority,
      alert: pi.alert,
    })),
    entityWatchlists: json.entity_watchlists,
    exclusions: json.exclusions.map(e => ({
      sourceDomain: e.source_domain,
      entityName: e.entity_name,
      reason: e.reason,
    })),
    ontologyExtensions: {
      assetSubtypes: json.ontology_extensions.asset_subtypes,
      customRelationships: json.ontology_extensions.custom_relationships,
    },
    webSources: json.web_sources?.map(ws => ({
      url: ws.url,
      name: ws.name,
      reliability: ws.reliability,
      scrapePattern: ws.scrape_pattern,
    })),
    searchConfig: json.search_config ? {
      searxngUrl: json.search_config.searxng_url,
      maxResults: json.search_config.max_results,
    } : undefined,
  };
}

export function deskConfigToJSON(config: DeskConfig): DeskConfigJSON {
  return {
    desk_id: config.deskId,
    version: config.version,
    name: config.name,
    description: config.description,
    created_by: config.createdBy,
    last_modified: config.lastModified,
    geographic_focus: config.geographicFocus,
    sources: config.sources.map(s => ({
      id: s.id,
      name: s.name,
      type: s.type,
      url: s.url,
      endpoint: s.endpoint,
      channel_id: s.channelId,
      check_interval_minutes: s.checkIntervalMinutes,
      reliability: s.reliability,
      enabled: s.enabled,
      notes: s.notes,
    })),
    priority_indicators: config.priorityIndicators.map(pi => ({
      id: pi.id,
      description: pi.description,
      entity_type: pi.entityType,
      conditions: pi.conditions,
      priority: pi.priority,
      alert: pi.alert,
    })),
    entity_watchlists: config.entityWatchlists,
    exclusions: config.exclusions.map(e => ({
      source_domain: e.sourceDomain,
      entity_name: e.entityName,
      reason: e.reason,
    })),
    ontology_extensions: {
      asset_subtypes: config.ontologyExtensions.assetSubtypes,
      custom_relationships: config.ontologyExtensions.customRelationships,
    },
    web_sources: config.webSources?.map(ws => ({
      url: ws.url,
      name: ws.name,
      reliability: ws.reliability,
      scrape_pattern: ws.scrapePattern,
    })),
    search_config: config.searchConfig ? {
      searxng_url: config.searchConfig.searxngUrl,
      max_results: config.searchConfig.maxResults,
    } : undefined,
  };
}

// ---------------------------------------------------------------------------
// Desk config validation
// ---------------------------------------------------------------------------

export interface DeskConfigValidationResult {
  valid: boolean;
  errors: string[];
}

export function validateDeskConfig(config: DeskConfig): DeskConfigValidationResult {
  const errors: string[] = [];

  if (!config.deskId) errors.push('deskId is required');
  if (!config.version) errors.push('version is required');
  if (!config.name) errors.push('name is required');

  // Geographic focus
  const bbox = config.geographicFocus?.primary?.coords;
  if (!bbox || bbox.length !== 4) {
    errors.push('geographic_focus.primary.coords must be [south, west, north, east]');
  } else {
    const [south, west, north, east] = bbox;
    if (south < -90 || south > 90) errors.push(`south bound ${south} out of range [-90, 90]`);
    if (north < -90 || north > 90) errors.push(`north bound ${north} out of range [-90, 90]`);
    if (west < -180 || west > 180) errors.push(`west bound ${west} out of range [-180, 180]`);
    if (east < -180 || east > 180) errors.push(`east bound ${east} out of range [-180, 180]`);
    if (south >= north) errors.push(`south (${south}) must be less than north (${north})`);
  }

  // Sources
  if (!config.sources || config.sources.length === 0) {
    errors.push('at least one source is required');
  } else {
    const sourceIds = new Set<string>();
    for (const src of config.sources) {
      if (!src.id) errors.push('source id is required');
      if (sourceIds.has(src.id)) errors.push(`duplicate source id: ${src.id}`);
      sourceIds.add(src.id);
      if (!src.name) errors.push(`source ${src.id}: name is required`);
      if (!src.reliability) errors.push(`source ${src.id}: reliability is required`);
      if (!['A', 'B', 'C', 'D', 'E', 'F'].includes(src.reliability)) {
        errors.push(`source ${src.id}: reliability must be A-F, got ${src.reliability}`);
      }
      if (src.checkIntervalMinutes < 1) {
        errors.push(`source ${src.id}: check_interval_minutes must be >= 1`);
      }
    }
  }

  // Priority indicators
  if (config.priorityIndicators) {
    const piIds = new Set<string>();
    for (const pi of config.priorityIndicators) {
      if (!pi.id) errors.push('priority_indicator id is required');
      if (piIds.has(pi.id)) errors.push(`duplicate priority_indicator id: ${pi.id}`);
      piIds.add(pi.id);
      if (!pi.entityType) errors.push(`priority_indicator ${pi.id}: entity_type is required`);
      if (!['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].includes(pi.priority)) {
        errors.push(`priority_indicator ${pi.id}: priority must be CRITICAL/HIGH/MEDIUM/LOW`);
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// Desk config store — load, get active, list
// ---------------------------------------------------------------------------

/** In-memory registry of loaded desk configs. */
const deskConfigs = new Map<string, DeskConfig>();
let activeDeskId: string | null = null;

/** Load a desk config from its parsed JSON form. */
export function loadDeskConfig(json: DeskConfigJSON): DeskConfig {
  const config = parseDeskConfigJSON(json);
  const validation = validateDeskConfig(config);
  if (!validation.valid) {
    console.warn(`[Sentinel] Desk config '${config.deskId}' has validation errors:`, validation.errors);
  }
  deskConfigs.set(config.deskId, config);
  console.log(`[Sentinel] Desk config loaded: ${config.name} (${config.deskId})`);
  return config;
}

/** Get a desk config by ID. */
export function getDeskConfig(deskId: string): DeskConfig | undefined {
  return deskConfigs.get(deskId);
}

/** Get all loaded desk config IDs. */
export function getDeskConfigIds(): string[] {
  return Array.from(deskConfigs.keys());
}

/** Set the active desk config for investigations. */
export function setActiveDeskConfig(deskId: string): void {
  if (!deskConfigs.has(deskId)) {
    throw new Error(`Desk config not found: ${deskId}`);
  }
  activeDeskId = deskId;
  console.log(`[Sentinel] Active desk config set: ${deskId}`);
}

/** Get the active desk config. Returns undefined if none set. */
export function getActiveDeskConfig(): DeskConfig | undefined {
  if (!activeDeskId) return undefined;
  return deskConfigs.get(activeDeskId);
}

/** Get the active desk config ID. */
export function getActiveDeskConfigId(): string | null {
  return activeDeskId;
}
